import * as monaco from "monaco-editor";
import { FC, createElement, useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom/client";
import { useSearchParams } from "react-router-dom";
import useEditor from "../../hooks/useEditor";

import { useTheme } from "@mui/material";
import { XCircleIcon } from "@phosphor-icons/react/dist/csr/XCircle";
import { EditorContainer } from "./styles";
import { copyToClipboard, getOrCreateModel } from "./utils";
interface IMonacoCodeEditorProps {
  value: string;
  path: string;
  onChange: (editorValue: string, event: monaco.editor.IModelContentChangedEvent) => void;
  position: number;
  height: string | number;
  width: string | number;
  markers?: monaco.editor.IMarkerData[];
  options?: monaco.editor.IEditorOptions;
  language?: string;
}

const CodeEditor: FC<IMonacoCodeEditorProps> = ({
  value,
  path,
  onChange,
  position,
  height,
  width,
  markers,
  options = {},
  language = "sbs",
}) => {
  const theme = useTheme();
  const valueRef = useRef<string>(value);
  const subscription = useRef<monaco.IDisposable | null | undefined>(null);
  const hasAppliedPermalinkSelection = useRef(false);
  const searchParamsRef = useRef(new URLSearchParams());
  const [searchParams, setSearchParams] = useSearchParams();
  const { editorRef, isEditorReady, monacoEl } = useEditor({
    options,
  });

  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  useEffect(() => {
    hasAppliedPermalinkSelection.current = false;
  }, [searchParams]);

  useEffect(() => {
    if (!position) return;
    editorRef.current?.setPosition({ lineNumber: position, column: 1 });
    editorRef.current?.focus();
  }, [position, editorRef]);

  useEffect(() => {
    const model = getOrCreateModel(value, language, path);

    editorRef.current?.setModel(model);
  }, [value, path, editorRef, language]);

  useEffect(() => {
    if (!editorRef.current || value === undefined) return;
    if (editorRef.current.getOption(monaco.editor.EditorOption.readOnly)) {
      editorRef.current.setValue(value);
    } else if (value !== editorRef.current.getValue()) {
      editorRef.current.executeEdits("", [
        {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          range: editorRef.current.getModel()!.getFullModelRange(),
          text: value,
          forceMoveMarkers: true,
        },
      ]);

      editorRef.current.pushUndoStop();
    }
  }, [value, editorRef]);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (isEditorReady && onChange) {
      subscription.current?.dispose();
      subscription.current = editorRef.current?.onDidChangeModelContent((event) => {
        const editorValue = editorRef.current?.getValue();

        if (valueRef.current !== editorValue) onChange(editorValue || "", event);
      });
    }
  }, [isEditorReady, onChange, editorRef]);
  useEffect(() => {
    const model = editorRef.current?.getModel();
    if (isEditorReady && model && markers) {
      const oldConflictingLines: Array<number> = [];
      monaco.editor.getModelMarkers({}).forEach((marker) => {
        oldConflictingLines.push(marker.startLineNumber);
      });
      monaco.editor.setModelMarkers(model, "", markers);
      oldConflictingLines.forEach((line) => {
        editorRef.current?.removeGlyphMarginWidget({
          getId: () => path + line,
          getDomNode: () => {
            const domNode = document.createElement("");
            return domNode;
          },
          getPosition: () => ({
            lane: 1,
            zIndex: 1,
            range: {
              startLineNumber: 1,
              endLineNumber: 1,
              startColumn: 1,
              endColumn: 1,
            },
          }),
        });
      });
      markers?.forEach((marker) => {
        if (marker.severity === monaco.MarkerSeverity.Error || marker.severity === monaco.MarkerSeverity.Warning) {
          editorRef?.current?.addGlyphMarginWidget({
            getId: () => path + marker.startLineNumber,
            getDomNode: () => {
              const domNode = document.createElement("div");
              const errorIcon = createElement(XCircleIcon, {
                color:
                  marker.severity === monaco.MarkerSeverity.Error
                    ? theme.palette.error.main
                    : theme.palette.warning.main,
              });
              ReactDOM.createRoot(domNode).render(errorIcon);
              return domNode;
            },
            getPosition: () => ({
              lane: 1,
              zIndex: 1,
              range: {
                startLineNumber: marker.startLineNumber,
                endLineNumber: marker.endLineNumber,
                startColumn: 1,
                endColumn: 1,
              },
            }),
          });
        }
      });
    }
  }, [isEditorReady, editorRef, markers, path, theme]);

  const setSelectedText = useCallback(() => {
    const lines = searchParams.get("lines");
    if (!lines || !isEditorReady || !editorRef.current || hasAppliedPermalinkSelection.current) return;

    const model = editorRef.current.getModel();
    if (!model || model.getLineCount() === 0) return;

    const linesAux = lines.split("-");
    const rowFrom = linesAux[0];
    const rowTo = linesAux[1];
    const colFrom = linesAux[2];
    const colTo = linesAux[3];

    editorRef.current.setSelection({
      startLineNumber: rowFrom ? parseInt(rowFrom) : 1,
      startColumn: colFrom ? parseInt(colFrom) : 1,
      endLineNumber: rowTo ? parseInt(rowTo) : 1,
      endColumn: colTo ? parseInt(colTo) : 1,
    });
    editorRef.current.revealLineInCenter(rowFrom ? parseInt(rowFrom) : 1);
    editorRef.current.focus();

    hasAppliedPermalinkSelection.current = true;
  }, [searchParams, isEditorReady, editorRef]);
  useEffect(() => {
    if (!isEditorReady || !editorRef) return;
    editorRef.current?.updateOptions(options);
    setSelectedText();

    if (!editorRef.current?.getAction("copy-link-to-clipboard")) {
      editorRef.current?.addAction({
        id: "copy-link-to-clipboard",
        label: "Copy permalink to code block",
        contextMenuGroupId: "navigation",
        run: copyToClipboard,
      });
    }

    const disposable = editorRef.current?.onDidChangeCursorSelection((e) => {
      if (e.source !== "api" && e.source !== "modelChange" && searchParamsRef.current.has("lines")) {
        setSearchParams((params) => {
          params.delete("lines");
          return params;
        });
      }
    });

    return () => disposable?.dispose();
  }, [editorRef, isEditorReady, options, setSelectedText, setSearchParams]);

  return <EditorContainer height={height} width={width} ref={monacoEl} data-cy="monaco-editor" />;
};

export default CodeEditor;
